import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  Plus, MessageSquare, Send, X,
  ChevronLeft, Paperclip, File, Loader2, AlertCircle
} from 'lucide-react';

// --- Badges ---
const PriorityBadge = ({ p }) => {
  const colors = { low: 'bg-blue-100 text-blue-700', medium: 'bg-yellow-100 text-yellow-700', high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700' };
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${colors[p] || colors.medium}`}>{p}</span>;
};

const StatusBadge = ({ s }) => {
    if (!s) return null;
    const colors = {
  open: 'bg-green-100 text-green-700',
  in_progress: 'bg-blue-100 text-blue-700',
  awaiting_reply: 'bg-purple-100 text-purple-700',
  resolved: 'bg-slate-100 text-slate-700',
  closed: 'bg-slate-200 text-slate-500'
};

return (
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${colors[s] || colors.open}`}>
        {s.replace('_', ' ')}
    </span>
);
};

const UserTicketsPage = () => {
  const queryClient = useQueryClient();
  const [view, setView] = useState('list');
  const [activeTicketUid, setActiveTicketUid] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const scrollRef = useRef(null);

  // Form States
  const [createForm, setCreateForm] = useState({ subject: '', message: '', priority: 'medium' });
  const [replyMsg, setReplyMsg] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // ✅ NEW: Files State

  // --- QUERIES ---
  const { data: tickets = [] } = useQuery({
    queryKey: ['myTickets'],
    queryFn: async () => {
        try {
            const res = await axiosClient.get('/support/tickets');
            return Array.isArray(res) ? res : [];
        } catch { return []; }
    }
  });

  const { data: conversation, isLoading: chatLoading, isError } = useQuery({
    queryKey: ['ticketChat', activeTicketUid],
    queryFn: () => axiosClient.get(`/support/tickets/${activeTicketUid}`),
    enabled: !!activeTicketUid,
    refetchInterval: 5000
  });

  // Auto-scroll
  useEffect(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversation]);

  // --- MUTATIONS ---
  const createMutation = useMutation({
      mutationFn: (data) => axiosClient.post('/support/tickets', data),
      onSuccess: () => {
          queryClient.invalidateQueries(['myTickets']);
          setShowCreateModal(false);
          setCreateForm({ subject: '', message: '', priority: 'medium' });
      }
  });

  const replyMutation = useMutation({
      mutationFn: (formData) => axiosClient.post(`/support/tickets/${activeTicketUid}/reply`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
      }),
      onSuccess: () => {
          queryClient.invalidateQueries(['ticketChat', activeTicketUid]);
          setReplyMsg('');
          setSelectedFiles([]);
      }
  });

  // Handle Reply Submit
  const handleReplySubmit = (e) => {
      e.preventDefault();
      if (!replyMsg.trim() && selectedFiles.length === 0) return;

      const formData = new FormData();
      formData.append('message', replyMsg);
      selectedFiles.forEach(file => formData.append('files', file));

      replyMutation.mutate(formData);
  };

  // --- RENDERERS ---

  // 1. Chat View
  if (view === 'chat' && activeTicketUid) {
      if (chatLoading) return <div className="h-64 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2"/> Loading Chat...</div>;
      if (isError || !conversation) return <div className="h-64 flex flex-col items-center justify-center text-red-400"><AlertCircle className="mb-2"/>Failed to load ticket.<button onClick={() => setView('list')} className="text-blue-500 underline mt-2">Go Back</button></div>;

      return (
        <div className="h-[calc(100vh-140px)] flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-in slide-in-from-right-4">
             {/* Chat Header */}
             <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                 <div className="flex items-center">
                    <button onClick={() => setView('list')} className="mr-3 p-1.5 hover:bg-slate-200 rounded-lg text-slate-500"><ChevronLeft className="w-5 h-5"/></button>
                    <div>
                        <h2 className="font-bold text-slate-800 text-lg flex items-center">
                            {conversation.ticket.subject}
                            <span className="ml-3"><PriorityBadge p={conversation.ticket.priority} /></span>
                        </h2>
                        <p className="text-xs text-slate-500 font-mono">ID: {activeTicketUid}</p>
                    </div>
                 </div>
                 <StatusBadge s={conversation.ticket.status} />
             </div>

             {/* Chat Messages */}
             <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50" ref={scrollRef}>
                 {conversation.replies.length === 0 ? (
                     <div className="text-center text-slate-400 mt-10 p-6 bg-white rounded-xl border border-dashed border-slate-200 w-fit mx-auto">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50"/>
                        <p>No messages yet. Describe your issue below.</p>
                     </div>
                 ) : (
                     conversation.replies.map((msg, i) => (
                         <div key={i} className={`flex ${msg.is_staff_reply ? 'justify-start' : 'justify-end'}`}>
                             <div className={`max-w-[70%] ${msg.is_staff_reply ? 'bg-white border border-slate-200 text-slate-700' : 'bg-blue-600 text-white'} p-4 rounded-2xl shadow-sm relative group`}>
                                 <div className="flex justify-between items-center mb-1 text-[10px] opacity-70 font-bold uppercase tracking-wider">
                                     <span>{msg.sender}</span>
                                     <span>{new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                 </div>
                                 <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                                 
                                 {/* ✅ Attachment Rendering */}
                                 {msg.attachment_urls && msg.attachment_urls.length > 0 && (
                                     <div className="mt-3 space-y-2">
                                         {msg.attachment_urls.map((url, idx) => (
                                             <a key={idx} href={url} target="_blank" rel="noreferrer" className="block">
                                                {url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                                    <img src={url} alt="Attachment" className="max-h-48 rounded-lg border border-white/20" />
                                                ) : (
                                                    <div className="flex items-center p-2 bg-black/10 rounded-lg text-xs font-mono">
                                                        <File className="w-4 h-4 mr-2"/> Attachment {idx+1}
                                                    </div>
                                                )}
                                             </a>
                                         ))}
                                     </div>
                                 )}
                             </div>
                         </div>
                     ))
                 )}
             </div>

             {/* Reply Input */}
             <div className="p-4 bg-white border-t border-slate-200">
                 {/* File Preview */}
                 {selectedFiles.length > 0 && (
                     <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
                         {Array.from(selectedFiles).map((f, i) => (
                             <div key={i} className="bg-slate-100 px-3 py-1 rounded-full text-xs flex items-center text-slate-600 border border-slate-200">
                                 {f.name} <button onClick={() => setSelectedFiles(selectedFiles.filter((_, idx) => idx !== i))} className="ml-2 hover:text-red-500"><X className="w-3 h-3"/></button>
                             </div>
                         ))}
                     </div>
                 )}

                 <form onSubmit={handleReplySubmit} className="flex gap-2 items-center">
                     <label className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl cursor-pointer transition-colors">
                         <input type="file" multiple className="hidden" onChange={(e) => setSelectedFiles([...selectedFiles, ...Array.from(e.target.files)])} />
                         <Paperclip className="w-5 h-5" />
                     </label>
                     <input 
                        className="flex-1 p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                        placeholder="Type your message..."
                        value={replyMsg}
                        onChange={(e) => setReplyMsg(e.target.value)}
                        disabled={conversation.ticket.status === 'closed'}
                     />
                     <button 
                        disabled={replyMutation.isLoading || (!replyMsg.trim() && selectedFiles.length === 0)} 
                        className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all shadow-lg shadow-blue-500/20"
                     >
                        {replyMutation.isLoading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Send className="w-5 h-5" />}
                     </button>
                 </form>
             </div>
        </div>
      );
  }

  // --- 2. LIST VIEW ---
  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <div className="flex justify-between items-center">
        <div><h1 className="text-2xl font-bold text-slate-800">Support Tickets</h1><p className="text-sm text-slate-500">We are here to help you 24/7.</p></div>
        <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 shadow-lg shadow-blue-500/20 font-bold text-sm">
          <Plus className="w-4 h-4 mr-2" /> New Ticket
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase">
             <tr>
                <th className="px-6 py-4">Subject</th>
                <th className="px-6 py-4">ID</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Priority</th>
                <th className="px-6 py-4">Last Update</th>
                <th className="px-6 py-4 text-right">Open</th>
             </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
             {tickets.length === 0 ? (
                 <tr><td colSpan="6" className="p-8 text-center text-slate-400">No tickets found.</td></tr>
             ) : (
                 tickets.map((t) => (
                    <tr key={t.ticket_uid} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => { setActiveTicketUid(t.ticket_uid); setView('chat'); }}>
                       <td className="px-6 py-4 font-semibold text-slate-700">{t.subject}</td>
                       <td className="px-6 py-4 font-mono text-xs text-slate-500">{t.ticket_uid}</td>
                       <td className="px-6 py-4"><StatusBadge s={t.status} /></td>
                       <td className="px-6 py-4"><PriorityBadge p={t.priority} /></td>
                       <td className="px-6 py-4 text-sm text-slate-500">{new Date(t.updated_at).toLocaleDateString()}</td>
                       <td className="px-6 py-4 text-right text-slate-400"><MessageSquare className="w-4 h-4 ml-auto" /></td>
                    </tr>
                 ))
             )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 backdrop-blur-sm p-4">
           <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
              <div className="flex justify-between items-center mb-6">
                 <h3 className="font-bold text-lg text-slate-800">Submit Support Request</h3>
                 <button onClick={() => setShowCreateModal(false)}><X className="w-5 h-5 text-slate-400"/></button>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(createForm); }} className="space-y-4">
                 <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">Subject</label><input className="w-full p-2.5 border rounded-lg" required value={createForm.subject} onChange={e => setCreateForm({...createForm, subject: e.target.value})} /></div>
                 <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">Priority</label><select className="w-full p-2.5 border rounded-lg bg-white" value={createForm.priority} onChange={e => setCreateForm({...createForm, priority: e.target.value})}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div>
                 <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">Message</label><textarea className="w-full p-2.5 border rounded-lg h-32" required value={createForm.message} onChange={e => setCreateForm({...createForm, message: e.target.value})} /></div>
                 <button disabled={createMutation.isLoading} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700">{createMutation.isLoading ? 'Submitting...' : 'Create Ticket'}</button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default UserTicketsPage;